// Shared Save / Save As / "save before leaving?" logic for a named, persisted
// document (a layout or a model). Tracks the entry you're currently editing by
// ID so:
//   • Save overwrites that entry silently (the caller's afterSave flashes it).
//   • The first Save (or any with no current entry) falls back to Save As.
//   • Save As always prompts for a name, and asks before overwriting a DIFFERENT
//     existing entry of the same name.
//   • confirmLeaveUnsaved() runs the 3-way Save / Don't save / Cancel prompt.
// The data-specific bits (build, store, dirty flag) come in via deps.

import { captureError } from "../app/telemetry";
import { showAlert, showConfirm, showPrompt, showSaveDiscardCancel } from "./modal";

export interface SaveSessionDeps<T> {
  noun: string; // "layout" / "model" — used in dialog copy
  isEmpty(): boolean; // nothing worth saving — skip the prompt entirely
  build(name: string): T | null; // build the saved object; null when empty (nothing to save)
  idOf(obj: T): string;
  nameOf(obj: T): string;
  createdAtOf(obj: T): number;
  reidentify(obj: T, id: string, createdAt: number): void; // make `obj` overwrite that entry
  list(): Promise<T[]>; // existing entries (for name-collision checks)
  put(obj: T): Promise<unknown>; // persist
  // Fired synchronously the instant a save is committed to, BEFORE the (slower)
  // build + persist — for immediate user feedback like flashing the Save button,
  // so the acknowledgement isn't delayed by the IndexedDB round-trip.
  onSaveStart?(): void;
  afterSave(obj: T): void; // refresh the dock + update the save UI once persisted
  isDirty(): boolean; // edited since the last save / load / new
  markClean(): void; // clear the caller's dirty flag (called after a successful save)
}

export interface SaveSession {
  save(): Promise<boolean>;
  saveAs(): Promise<boolean>;
  setCurrent(id: string, name: string, createdAt: number): void; // on load / import
  clearCurrent(): void; // on New
  confirmLeaveUnsaved(): Promise<"save" | "discard" | "cancel">;
  hasUnsavedWork(): boolean;
  currentName(): string;
  currentId(): string | null; // id of the open document (null = never saved/loaded)
}

export function createSaveSession<T>(deps: SaveSessionDeps<T>): SaveSession {
  let curId: string | null = null;
  let curName = "";
  let curCreatedAt = 0;

  const cap = deps.noun.charAt(0).toUpperCase() + deps.noun.slice(1);

  // Persist failures (storage quota, private-browsing restrictions) must never
  // look like a successful save — the flash has already played by the time the
  // IndexedDB round-trip rejects, so surface the failure loudly and report the
  // save as not-done (the dirty flag stays set, the work stays open).
  async function persist(obj: T): Promise<boolean> {
    try {
      await deps.put(obj);
      return true;
    } catch (e) {
      captureError(e); // telemetry (no-op unless configured) — this catch hides it from global handlers
      await showAlert(
        `Saving the ${deps.noun} failed — your browser may be blocking storage ` +
          `(private browsing?) or out of space. Your work is still open; try ` +
          `again, or export it to a file.\n\n(${(e as Error)?.message ?? e})`,
        { title: `${cap} not saved` },
      );
      return false;
    }
  }

  async function saveAs(): Promise<boolean> {
    if (deps.isEmpty()) return false; // nothing to save — don't bother prompting
    const name = (
      await showPrompt("", {
        title: `Save ${deps.noun}`,
        placeholder: `${cap} name`,
        initial: curName,
        confirmLabel: "Save",
      })
    )?.trim();
    if (!name) return false;
    const obj = deps.build(name);
    if (!obj) return false; // nothing to save
    const existing = (await deps.list()).find(
      (p) => deps.nameOf(p).trim().toLowerCase() === name.toLowerCase(),
    );
    // Confirm only when overwriting a DIFFERENT existing entry of that name.
    if (existing && deps.idOf(existing) !== curId) {
      const ok = await showConfirm(`A ${deps.noun} named “${name}” already exists.`, {
        title: "Overwrite it?",
        confirmLabel: "Overwrite",
        danger: true,
      });
      if (!ok) return false;
    }
    if (existing) deps.reidentify(obj, deps.idOf(existing), deps.createdAtOf(existing));
    deps.onSaveStart?.(); // acknowledge before the persist round-trip
    if (!(await persist(obj))) return false;
    curId = deps.idOf(obj);
    curName = name;
    curCreatedAt = deps.createdAtOf(obj);
    deps.markClean();
    deps.afterSave(obj);
    return true;
  }

  async function save(): Promise<boolean> {
    if (deps.isEmpty()) return false; // nothing to save
    if (curId === null) return saveAs(); // never saved → prompt
    deps.onSaveStart?.(); // acknowledge on click, before the (slower) build + persist
    const obj = deps.build(curName);
    if (!obj) return false;
    deps.reidentify(obj, curId, curCreatedAt); // overwrite the current entry
    if (!(await persist(obj))) return false;
    deps.markClean();
    deps.afterSave(obj);
    return true;
  }

  const session: SaveSession = {
    save,
    saveAs,
    setCurrent(id, name, createdAt) {
      curId = id;
      curName = name;
      curCreatedAt = createdAt;
    },
    clearCurrent() {
      curId = null;
      curName = "";
      curCreatedAt = 0;
    },
    async confirmLeaveUnsaved() {
      // Nothing unsaved (or nothing worth saving) → just proceed.
      if (!deps.isDirty() || deps.isEmpty()) return "discard";
      const choice = await showSaveDiscardCancel(
        curId === null
          ? `Your ${deps.noun} hasn't been saved yet.`
          : `You have unsaved changes to this ${deps.noun}.`,
        { title: `Save this ${deps.noun}?` },
      );
      if (choice !== "save") return choice; // "discard" | "cancel"
      // A cancelled name prompt during Save means don't proceed.
      return (await save()) ? "save" : "cancel";
    },
    hasUnsavedWork: () => deps.isDirty() && !deps.isEmpty(),
    currentName: () => curName,
    currentId: () => curId,
  };
  return session;
}
